import { useCallback, useEffect, useState } from "react";

import BackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { Chip, IconButton, Typography } from "@mui/material";
import Grid from "@mui/material/Grid2";
import capitalize from "lodash/capitalize";

import { globalText } from "../../assets/texts";
import { templateLibraryTxt } from "../../assets/texts/CloudAnalytics";
import ResourceEditDialog from "./ResourceEditDialog";

export const defaultNameLength = 64;
export const defaultDescriptionLength = 1000;

const styles = {
  templateChip: {
    backgroundColor: "#9575CD",
    color: "white",
    height: 20,
  },
  resourceName: (name: string) => ({
    color: !name ? "text.disabled" : "text.primary",
    minWidth: 0,
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
  description: (hasDescription: boolean) => ({
    color: hasDescription ? "text.secondary" : "text.disabled",
    minHeight: "1.5rem",
    minWidth: 0,
    maxWidth: {
      xs: "100%",
      sm: "500px",
    },
    overflow: "hidden",
    textOverflow: "ellipsis",
  }),
};

export type Props = {
  name: string;
  description?: string;
  maxNameLength?: number;
  maxDescriptionLength?: number;
  showTemplateChip?: boolean;
  disabled?: boolean;
  resourceName: string;
  hideDescription?: boolean;
  customActionLabel?: string;
  onSave: (values: { name: string; description?: string }) => void;
  onBackButtonClicked: () => void;
};

export const EditableResourceHeader = ({
  name,
  description,
  maxNameLength = defaultNameLength,
  maxDescriptionLength = defaultDescriptionLength,
  showTemplateChip,
  disabled,
  resourceName = "resource",
  hideDescription = false,
  customActionLabel,
  onSave,
  onBackButtonClicked,
}: Props) => {
  const [isEditing, setIsEditing] = useState(false);
  const [displayName, setDisplayName] = useState(name);
  const [displayDescription, setDisplayDescription] = useState(description);
  const [editValues, setEditValues] = useState({ name, description });
  const showDescriptionPlaceholder = !hideDescription && displayDescription;

  useEffect(() => {
    if (!isEditing) {
      setDisplayName(name);
      setDisplayDescription(description);
    }
  }, [name, description, isEditing]);

  const handleStartEditing = useCallback(() => {
    setEditValues({ name: displayName, description: displayDescription });
    setIsEditing(true);
  }, [displayName, displayDescription]);

  const handleCancelEditing = useCallback(() => {
    setIsEditing(false);
  }, []);

  const handleSave = useCallback(
    (values: { name: string; description?: string }) => {
      onSave(values);
      setIsEditing(false);
      setDisplayName(values.name);
      setDisplayDescription(values.description);
    },
    [onSave]
  );

  return (
    <>
      <Grid container spacing={1} wrap="nowrap" sx={{ minWidth: 0, width: "inherit" }}>
        <Grid flex="0 0 auto" sx={{ display: "flex", alignItems: "center" }}>
          <IconButton sx={{ p: 0.5 }} aria-label="Back" size="large" onClick={onBackButtonClicked} data-cy="back-icon">
            <BackIcon fontSize="medium" color="action" />
          </IconButton>
        </Grid>
        <Grid flex="1 1 auto" sx={{ minWidth: 0, overflow: "hidden" }} alignContent="center">
          <Grid container spacing={1} alignItems="center" wrap="nowrap" sx={{ minWidth: 0 }}>
            <Grid flex="1 1 auto" sx={{ minWidth: 0, display: "flex", alignItems: "center" }}>
              <Typography variant="h4" data-cy="resource-name" noWrap sx={styles.resourceName(displayName)}>
                {displayName || `${capitalize(resourceName)} name` || ""}
              </Typography>
              {!disabled && (
                <IconButton
                  onClick={handleStartEditing}
                  size="small"
                  color="primary"
                  data-cy="resource-edit-button"
                  sx={{ ml: 0.5 }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              )}
            </Grid>
            {showTemplateChip && (
              <Grid flex="0 0 auto">
                <Chip sx={styles.templateChip} label={templateLibraryTxt.TEMPLATE} data-cy="template-chip" />
              </Grid>
            )}
          </Grid>
          {showDescriptionPlaceholder && (
            <Typography
              variant="subtitle2"
              noWrap
              sx={styles.description(!!displayDescription)}
              data-cy="resource-description"
            >
              {displayDescription || globalText.ADD_DESCRIPTION}
            </Typography>
          )}
        </Grid>
      </Grid>

      <ResourceEditDialog
        open={isEditing}
        onClose={handleCancelEditing}
        onSave={handleSave}
        initialValues={editValues}
        maxNameLength={maxNameLength}
        maxDescriptionLength={maxDescriptionLength}
        resourceName={resourceName}
        hideDescription={hideDescription}
        customActionLabel={customActionLabel}
      />
    </>
  );
};
